import React, { useEffect, useMemo, useState } from "react";
import api, { formatError } from "@/lib/api";
import InventoryCard from "@/components/InventoryCard";
import SucursalPicker from "@/components/SucursalPicker";
import { useSucursalGate } from "@/hooks/useSucursalGate";
import { toast } from "sonner";
import { Loader2, PackageOpen, ArrowLeft, Store } from "lucide-react";

/**
 * Category-based inventory page. `category` prop is "piso" or "produccion".
 * If the user has multiple sucursales available, show the picker first.
 */
export default function Dashboard({ category = "piso" }) {
  const gate = useSucursalGate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async (sucursalId) => {
    if (!sucursalId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/items?category=${category}&sucursal_id=${sucursalId}`);
      setItems(data);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Error cargando datos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (gate.current?.id) load(gate.current.id);
    else setItems([]);
  }, [gate.current?.id, category]);

  const handleToggle = async (item) => {
    try {
      const { data } = await api.post(`/items/${item.id}/toggle-reorder`);
      setItems((prev) => prev.map((it) => (it.id === data.id ? data : it)));
      if (data.needs_reorder) {
        toast.success(`"${data.name}" marcado para reordenar`, {
          description: "Se notificó al Gerente.",
        });
      } else {
        toast.success(`"${data.name}" marcado como reordenado`);
      }
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Error");
    }
  };

  const pendingCount = items.filter((i) => i.needs_reorder).length;

  const title = category === "produccion" ? "Producción" : "Piso";
  const pickerSubtitle =
    category === "produccion"
      ? "¿A qué sucursal vas a producir?"
      : "¿En qué sucursal estás trabajando?";
  const listSubtitle =
    category === "produccion"
      ? "Toca una tarjeta si te falta algo en cocina / bodega."
      : "Toca una tarjeta para marcar que se debe reordenar del piso.";

  if (gate.loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-[#FF7878]" />
      </div>
    );
  }

  // Show picker when no sucursal picked AND user has multiple options
  if (!gate.current) {
    return (
      <SucursalPicker
        sucursales={gate.sucursales}
        onPick={gate.setCurrent}
        title={title}
        subtitle={pickerSubtitle}
      />
    );
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div className="min-w-0 flex-1">
          {gate.sucursales.length > 1 && (
            <button
              type="button"
              onClick={gate.clear}
              data-testid="change-sucursal-chip"
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#8A796F] hover:text-[#FF5C5C] mb-2 transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Cambiar sucursal
            </button>
          )}
          <h1
            className="font-display text-3xl sm:text-4xl font-black tracking-tight text-[#4A3B32]"
            data-testid={`${category}-title`}
          >
            {title}
          </h1>
          <p className="text-[#8A796F] mt-1 flex items-center gap-1.5">
            <Store className="w-3.5 h-3.5" />
            <span className="font-semibold text-[#4A3B32]">{gate.current.name}</span>
            <span className="hidden sm:inline">— {listSubtitle}</span>
          </p>
        </div>
        <div className="px-4 py-2 rounded-2xl bg-white border border-[#EADBCE] shrink-0">
          <div className="text-xs text-[#8A796F] font-semibold uppercase tracking-wide">Pendientes</div>
          <div
            className="text-2xl font-display font-extrabold text-[#FF5C5C]"
            data-testid={`${category}-pending-count`}
          >
            {pendingCount}
          </div>
        </div>
      </header>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-[#FF7878]" />
        </div>
      ) : (
        <Grid items={items} onToggle={handleToggle} category={category} />
      )}
    </div>
  );
}

function Grid({ items, onToggle, category }) {
  if (!items || items.length === 0) {
    return (
      <div
        className="bg-white border border-dashed border-[#EADBCE] rounded-3xl py-16 text-center"
        data-testid={`${category}-empty`}
      >
        <PackageOpen className="mx-auto w-12 h-12 text-[#EADBCE]" />
        <h3 className="mt-4 font-display font-bold text-lg text-[#4A3B32]">Sin items por aquí</h3>
        <p className="text-sm text-[#8A796F] mt-1">
          El Gerente puede agregar items en Configuración.
        </p>
      </div>
    );
  }
  return (
    <div
      className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 sm:gap-6"
      data-testid="items-grid"
    >
      {items.map((it) => (
        <InventoryCard key={it.id} item={it} onToggle={onToggle} />
      ))}
    </div>
  );
}
